import React from "react";
import ForgeShell, { ControlLabel, Segmented, Chips, Textarea, OutputCard, CopyableText, EmptyState } from "./ForgeShell";
import { useForgeModule, GenerateButton, ActionRow, DraftsList } from "./useForgeModule";

const CONTENT_TYPES = [
  { value: "reel", label: "Reel" },
  { value: "carousel", label: "Carousel" },
  { value: "story", label: "Story" },
  { value: "linkedin", label: "LinkedIn" },
  { value: "twitter", label: "X / Twitter" },
  { value: "email", label: "Email" },
  { value: "ad_script", label: "Ad Script" },
];
const PLATFORMS = ["Instagram", "TikTok", "YouTube", "LinkedIn", "X", "Email"];
const TONES = ["Confident", "Calm", "Playful", "Premium", "Urgent", "Educational"];

export default function ContentLab() {
  const m = useForgeModule("content");
  const i = m.inputs;
  const set = (k, v) => m.setInputs({ ...i, [k]: v });

  return (
    <ForgeShell
      testid="forge-content-lab"
      left={
        <>
          <ControlLabel hint="Where the post lives">Platform</ControlLabel>
          <Chips testid="content-platform" options={PLATFORMS} value={i.platform || "Instagram"} onChange={(v) => set("platform", v)} />

          <ControlLabel hint="Format of the asset">Content type</ControlLabel>
          <Segmented testid="content-type" options={CONTENT_TYPES} value={i.type || "reel"} onChange={(v) => set("type", v)} />

          <ControlLabel hint="What the audience walks away with">Topic / outcome</ControlLabel>
          <Textarea testid="content-topic" value={i.topic} onChange={(v) => set("topic", v)} rows={4}
            placeholder="3 ways creators 10× their revenue without more content" />

          <ControlLabel>Tone</ControlLabel>
          <Chips testid="content-tone" options={TONES} value={i.tone || "Confident"} onChange={(v) => set("tone", v)} />

          <GenerateButton testid="content-generate" loading={m.loading} onClick={m.generate} />
        </>
      }
      center={
        !m.output ? (
          <EmptyState title="Generate your first content set" body="Hooks, caption, CTA, hashtags and 3 alternates — all shaped to your brand voice." />
        ) : (
          <>
            <ActionRow testid="content-actions" onRegenerate={m.generate} onSave={() => m.saveDraft((m.output.hooks || [])[0])} regenerating={m.loading} />

            <OutputCard title="Hooks">
              <ul className="space-y-2">
                {(m.output.hooks || []).map((h, idx) => <li key={idx}><CopyableText testid={`content-hook-${idx}`} text={h} /></li>)}
              </ul>
            </OutputCard>

            <div className="grid md:grid-cols-2 gap-4">
              <OutputCard title="Caption">
                <CopyableText testid="content-caption" text={m.output.caption} />
              </OutputCard>
              <OutputCard title="CTA">
                <CopyableText testid="content-cta" text={m.output.cta} />
              </OutputCard>
            </div>

            <OutputCard title="Hashtags">
              <div className="flex flex-wrap gap-1.5">
                {(m.output.hashtags || []).map((tag, idx) => (
                  <span key={idx} className="pill pill-neutral font-mono text-[11px]">{tag}</span>
                ))}
              </div>
            </OutputCard>

            <OutputCard title="Alternate variations">
              <ul className="space-y-2">
                {(m.output.alt_variations || []).map((v, idx) => <li key={idx}><CopyableText testid={`content-alt-${idx}`} text={v} /></li>)}
              </ul>
            </OutputCard>
          </>
        )
      }
      right={
        <>
          <div>
            <ControlLabel hint="Trained from your Nexus voice memory">Brand voice</ControlLabel>
            <p className="text-xs text-ink-soft mt-2 leading-relaxed">
              Direct, confident, no fluff. Speak to the founder-creator who reads at 6am.
            </p>
          </div>
          <DraftsList drafts={m.drafts} onLoad={m.loadDraft} onDelete={m.deleteDraft}
            onToggleFavorite={m.toggleFavorite} onArchive={m.archiveDraft}
            onDuplicate={m.duplicateDraft} onPromote={m.promoteDraft} module="content" />
        </>
      }
    />
  );
}
